if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      if (window.matchMedia('(min-width: 769px)').matches) {
        return;
      }

      this.productSection = this.closest('.cc-main-product');
      if (!this.productSection) return;

      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      if (!this.productForm) return;

      this.mainQtyInput = this.productSection.querySelector('.qty-input__input, input[name="quantity"]');
      this.mainAddButton = this.productSection.querySelector('.product-info__add-button button[type="submit"]');
      this.mainVariantInput = this.productSection.querySelector('input[name="id"]');

      this.stickyQtyInput = this.querySelector('[data-sticky-qty-input]');
      this.stickyQtyMinus = this.querySelector('[data-sticky-qty-minus]');
      this.stickyQtyPlus = this.querySelector('[data-sticky-qty-plus]');
      this.stickySubmit = this.querySelector('[data-sticky-atc-submit]');
      this.stickyTitle = this.querySelector('[data-sticky-atc-title]');
      this.variantJsonEl = this.productSection.querySelector('[data-product-variants-json]');

      this.handleMainQtyInput = this.handleMainQtyInput.bind(this);
      this.handleStickyQtyInput = this.handleStickyQtyInput.bind(this);
      this.handleStickySubmit = this.handleStickySubmit.bind(this);
      this.handleStickyMinus = this.handleStickyMinus.bind(this);
      this.handleStickyPlus = this.handleStickyPlus.bind(this);
      this.handleVariantOrFormChange = this.handleVariantOrFormChange.bind(this);

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);

      if (this.mainQtyInput) {
        this.mainQtyInput.removeEventListener('input', this.handleMainQtyInput);
        this.mainQtyInput.removeEventListener('change', this.handleMainQtyInput);
      }

      if (this.stickyQtyInput) {
        this.stickyQtyInput.removeEventListener('input', this.handleStickyQtyInput);
        this.stickyQtyInput.removeEventListener('change', this.handleStickyQtyInput);
      }

      if (this.stickyQtyMinus) {
        this.stickyQtyMinus.removeEventListener('click', this.handleStickyMinus);
      }

      if (this.stickyQtyPlus) {
        this.stickyQtyPlus.removeEventListener('click', this.handleStickyPlus);
      }

      if (this.stickySubmit) {
        this.stickySubmit.removeEventListener('click', this.handleStickySubmit);
      }

      if (this.productSection) {
        this.productSection.removeEventListener('change', this.handleVariantOrFormChange);
      }

      if (this.buttonObserver) this.buttonObserver.disconnect();
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      this.bindStickyControls();
      this.syncFromMainQty();
      this.syncButtonState();
      this.updateQtyButtons();
      this.updateStickyTitle();

      this.observeMainButton();
    }

    bindStickyControls() {
      if (this.mainQtyInput) {
        this.mainQtyInput.addEventListener('input', this.handleMainQtyInput);
        this.mainQtyInput.addEventListener('change', this.handleMainQtyInput);
      }

      if (this.stickyQtyInput) {
        this.stickyQtyInput.addEventListener('input', this.handleStickyQtyInput);
        this.stickyQtyInput.addEventListener('change', this.handleStickyQtyInput);
      }

      if (this.stickyQtyMinus) {
        this.stickyQtyMinus.addEventListener('click', this.handleStickyMinus);
      }

      if (this.stickyQtyPlus) {
        this.stickyQtyPlus.addEventListener('click', this.handleStickyPlus);
      }

      if (this.stickySubmit) {
        this.stickySubmit.addEventListener('click', this.handleStickySubmit);
      }

      this.productSection.addEventListener('change', this.handleVariantOrFormChange);
    }

    observeMainButton() {
      if (!this.mainAddButton) return;

      this.buttonObserver = new MutationObserver(() => {
        this.syncButtonState();
      });

      this.buttonObserver.observe(this.mainAddButton, {
        attributes: true,
        attributeFilter: ['class', 'disabled']
      });
    }

    getMinQty() {
      if (!this.mainQtyInput) return 1;
      return parseInt(this.mainQtyInput.getAttribute('min'), 10) || 1;
    }

    getMaxQty() {
      if (!this.mainQtyInput) return null;

      const maxAttr = this.mainQtyInput.getAttribute('max');
      if (maxAttr === null || maxAttr === '') return null;

      const max = parseInt(maxAttr, 10);
      return Number.isNaN(max) ? null : max;
    }

    clampQty(value) {
      const min = this.getMinQty();
      const max = this.getMaxQty();

      let qty = parseInt(value, 10);
      if (Number.isNaN(qty)) qty = min;
      if (qty < min) qty = min;
      if (max !== null && qty > max) qty = max;

      return qty;
    }

    setBothQty(value) {
      const qty = this.clampQty(value);

      if (this.stickyQtyInput) {
        this.stickyQtyInput.value = qty;
      }

      if (this.mainQtyInput) {
        if (String(this.mainQtyInput.value) !== String(qty)) {
          this.mainQtyInput.value = qty;
          this.mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.updateQtyButtons();
    }

    syncFromMainQty() {
      if (!this.mainQtyInput || !this.stickyQtyInput) return;

      const qty = this.clampQty(this.mainQtyInput.value || 1);
      this.stickyQtyInput.value = qty;
      this.updateQtyButtons();
    }

    updateQtyButtons() {
      if (!this.stickyQtyInput) return;

      const current = this.clampQty(this.stickyQtyInput.value || 1);
      const min = this.getMinQty();
      const max = this.getMaxQty();

      if (this.stickyQtyMinus) {
        this.stickyQtyMinus.disabled = current <= min;
      }

      if (this.stickyQtyPlus) {
        this.stickyQtyPlus.disabled = max !== null ? current >= max : false;
      }
    }

    syncButtonState() {
      if (!this.mainAddButton || !this.stickySubmit) return;

      this.stickySubmit.disabled = this.mainAddButton.disabled;
      this.stickySubmit.innerHTML = this.mainAddButton.innerHTML;
      this.stickySubmit.classList.toggle('is-loading', this.mainAddButton.classList.contains('is-loading'));
      this.stickySubmit.classList.toggle('is-success', this.mainAddButton.classList.contains('is-success'));
      this.stickySubmit.classList.toggle('is-info-success', this.mainAddButton.classList.contains('is-info-success'));
    }

    handleMainQtyInput() {
      this.syncFromMainQty();
    }

    handleStickyQtyInput() {
      this.setBothQty(this.stickyQtyInput.value);
    }

    handleStickyMinus() {
      const current = parseInt(this.stickyQtyInput.value, 10) || 1;
      this.setBothQty(current - 1);
    }

    handleStickyPlus() {
      const current = parseInt(this.stickyQtyInput.value, 10) || 1;
      this.setBothQty(current + 1);
    }

    handleStickySubmit() {
      if (!this.mainAddButton || this.mainAddButton.disabled) return;

      this.setBothQty(this.stickyQtyInput ? this.stickyQtyInput.value : 1);
      this.mainAddButton.click();
      this.syncButtonState();
    }

    handleVariantOrFormChange(event) {
      const target = event.target;

      if (!target) return;

      if (
        target.matches('select, input[type="radio"], input[name="id"], input[name="quantity"]') ||
        target.closest('.product-options') ||
        target.closest('product-form')
      ) {
        window.requestAnimationFrame(() => {
          this.mainQtyInput = this.productSection.querySelector('.qty-input__input, input[name="quantity"]');
          this.mainAddButton = this.productSection.querySelector('.product-info__add-button button[type="submit"]');
          this.mainVariantInput = this.productSection.querySelector('input[name="id"]');

          this.syncFromMainQty();
          this.syncButtonState();
          this.updateQtyButtons();
          this.updateStickyTitle();
        });
      }
    }

    updateStickyTitle() {
      if (!this.stickyTitle) return;

      const mainVariantInput = this.mainVariantInput || this.productSection.querySelector('input[name="id"]');
      if (!mainVariantInput) return;

      const variantId = String(mainVariantInput.value || '');
      if (!variantId) return;

      let variants = [];

      if (this.variantJsonEl) {
        try {
          variants = JSON.parse(this.variantJsonEl.textContent);
        } catch (e) {
          return;
        }
      }

      if (!Array.isArray(variants) || !variants.length) return;

      const variant = variants.find((v) => String(v.id) === variantId);
      if (!variant) return;

      const fallbackTitle = this.stickyTitle.getAttribute('data-product-title') || this.stickyTitle.textContent.trim();

      this.stickyTitle.textContent =
        variant.title && variant.title !== 'Default Title'
          ? variant.title
          : fallbackTitle;
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}